import {CitySlug, getCityOption} from "../../../../../config/cities";
import {DistrictStats} from "../api/charts_platform_api";

const cityOutlineSlugKeys = (chartsCitySlug: CitySlug): string[] => {
    const {requestMapSlug, requestCitySlug} = getCityOption(chartsCitySlug);

    return Array.from(new Set([requestMapSlug, requestCitySlug, chartsCitySlug]));
};

export const resolveMapMetric = (value: unknown): number | undefined => {
    if (typeof value === "number" && !Number.isNaN(value)) {
        return value;
    }

    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);

        return Number.isNaN(parsed) ? undefined : parsed;
    }

    return undefined;
};

export const statsRecordSlugKeys = (record?: DistrictStats | Record<string, string | number> | null): string[] => {
    if (record == null) {
        return [];
    }

    const keys: string[] = [];

    for (const field of ["slug_agglomeration", "slug_city", "slug_district"] as const) {
        const value = record[field];

        if (typeof value === "string" && value.trim()) {
            keys.push(value.trim());
        }
    }

    return keys;
};

/**
 * District slugs from `month-district-stats` when available; otherwise a single city-level
 * metric for outline mode (`toCityOutlineOsmMapParams`) so the region polygon can still be filled.
 */
export const buildDistrictMapValuesBySlug = ({
    districtsMapAvailable,
    monthDistrictData,
    chartsCitySlug,
    districtMetric,
    cityLevelMetric,
    cityLevelStatsRecord,
    extraSlugKeys = []
}: {
    districtsMapAvailable: boolean;
    monthDistrictData: DistrictStats[];
    chartsCitySlug: CitySlug | null;
    districtMetric: (district: DistrictStats) => number | null | undefined;
    cityLevelMetric?: number | null;
    cityLevelStatsRecord?: DistrictStats | Record<string, string | number> | null;
    extraSlugKeys?: Array<string | null | undefined>;
}): Record<string, number> | undefined => {
    if (!districtsMapAvailable) {
        return undefined;
    }

    if (monthDistrictData.length > 0) {
        const entries = monthDistrictData.flatMap((district) => {
            const metric = districtMetric(district);

            return metric != null ? [[district.slug_district, metric] as const] : [];
        });

        return entries.length > 0 ? Object.fromEntries(entries) : undefined;
    }

    const metric = resolveMapMetric(cityLevelMetric);

    if (metric == null || chartsCitySlug == null) {
        return undefined;
    }

    const slugKeys = [
        ...cityOutlineSlugKeys(chartsCitySlug),
        ...statsRecordSlugKeys(cityLevelStatsRecord),
        ...extraSlugKeys.filter((slug): slug is string => typeof slug === "string" && slug.length > 0)
    ];

    return Object.fromEntries(Array.from(new Set(slugKeys)).map((slug) => [slug, metric]));
};
